import authSvc from '@efex-services/auth/authSvc';
import {
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useApiCall } from 'use-api-call';

interface IFormProvider {
  children: JSX.Element;
}

interface FormContextType {
  data: Record<string, unknown>;
  countries: unknown;
  federalEntities: unknown;
  birthcountries: unknown;
  setFormValues: (values: Record<string, unknown>) => void;
  getCountries: (value: string) => void;
  getFederalEntities: (value: string) => void;
  getBirthCountries: (value: string) => void;
}

export const FormContext = createContext<FormContextType | null>(null);

export const FormProvider = ({ children }: IFormProvider) => {
  const [data, setData] = useState<Record<string, unknown>>({});

  const { data: countries, invoke: getCountries } = useApiCall(
    (value: string) => authSvc.getCountries({ value })
  );

  const { data: birthcountries, invoke: getBirthCountries } = useApiCall(
    (value: string) => authSvc.getCountries({ value })
  );

  const { data: federalEntities, invoke: getFederalEntities } = useApiCall(
    (value: string) => authSvc.getFederalEntities({ value })
  );

  const setFormValues = useCallback(
    (values: Record<string, unknown>) => {
      setData((prevValues) => ({
        ...prevValues,
        ...values,
      }));
    },
    [setData]
  );

  useEffect(() => {
    getCountries();
    getBirthCountries();
    getFederalEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <FormContext.Provider
      value={useMemo(
        () => ({
          data,
          countries,
          federalEntities,
          birthcountries,
          setFormValues,
          getCountries,
          getFederalEntities,
          getBirthCountries,
        }),
        [
          data,
          countries,
          federalEntities,
          birthcountries,
          setFormValues,
          getCountries,
          getFederalEntities,
          getBirthCountries,
        ]
      )}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormData = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormData must be used within a FormProvider');
  }
  return context;
};
